import React from "react";
import dayjs from "dayjs";

/**
 * Ô hiển thị ngày hẹn: giữ nguyên ngày giờ chính xác và gắn thêm một thẻ
 * tương đối (Hôm nay / Hôm qua / Ngày mai / Đã qua / Còn N ngày) để quản trị
 * nhận biết nhanh mà không phải nhẩm ngày.
 */

type RelativeTone = {
    label: string;
    className: string;
};

/** Nhãn + màu của thẻ, tính theo số ngày lệch so với hôm nay */
export const getScheduleRelativeTone = (value?: string | null): RelativeTone | null => {
    if (!value) return null;
    const date = dayjs(String(value).replace(" ", "T"));
    if (!date.isValid()) return null;

    const diff = date.startOf("day").diff(dayjs().startOf("day"), "day");

    if (diff === 0) {
        return {
            label: "Hôm nay",
            className:
                "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800",
        };
    }
    if (diff === 1) {
        return {
            label: "Ngày mai",
            className:
                "bg-green-50 text-green-600 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800",
        };
    }
    if (diff === -1) {
        return {
            label: "Hôm qua",
            className:
                "bg-amber-50 text-amber-600 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-800",
        };
    }
    if (diff < -1) {
        return {
            label: `Đã qua ${Math.abs(diff)} ngày`,
            className:
                "bg-gray-100 text-gray-500 border-gray-200 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-700",
        };
    }
    return {
        label: `Còn ${diff} ngày`,
        className:
            "bg-sky-50 text-sky-600 border-sky-200 dark:bg-sky-900/30 dark:text-sky-300 dark:border-sky-800",
    };
};

interface ScheduleDateCellProps {
    value?: string | null;
    /** Định dạng ngày giờ hiển thị; mặc định giữ nguyên kiểu cũ của bảng */
    format?: (value: string) => string;
    className?: string;
}

const ScheduleDateCell: React.FC<ScheduleDateCellProps> = ({ value, format, className }) => {
    if (!value) return <span className="text-gray-400">—</span>;

    const tone = getScheduleRelativeTone(value);
    const text = format ? format(value) : new Date(value).toLocaleString("vi-VN");

    return (
        <div className={`flex flex-col items-start gap-1 ${className || ""}`}>
            <span className="text-gray-600 dark:text-gray-300 whitespace-nowrap">{text}</span>
            {tone && (
                <span
                    className={`inline-flex items-center px-1.5 py-[1px] rounded border text-[11px] font-medium leading-4 whitespace-nowrap ${tone.className}`}
                >
                    {tone.label}
                </span>
            )}
        </div>
    );
};

export default ScheduleDateCell;
